import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export type ButtonVariant =
  /** Pōhutukawa fill — the single strongest action on a screen (Search, Book, Publish). */
  | 'cta'
  /** Ngahere fill — forward motion inside a flow (Continue), and the send action. */
  | 'primary'
  /** White with a hairline — the neutral partner to a primary (Back, Sort). */
  | 'secondary'
  /** White with a ngahere hairline and ngahere label (Message driver). */
  | 'outline'
  /** Moana fill — Smolit's send button. */
  | 'teal'
  /** Moana tint — every "ask Smolit" affordance. */
  | 'tealSoft'
  /** Pōhutukawa on its tint — the trips dashboard's "Rate trip". */
  | 'ctaSoft';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl' | 'hero';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Stretch to the container width — the booking card's buttons do this. */
  block?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

/**
 * Height steps come straight from the export: 38px in the header, 44px in the
 * compact search bar, 46px on publish and secondary actions, 48px on the
 * confirmation screen, 50px for "Book this seat".
 */
const SIZE: Record<ButtonSize, string> = {
  sm: 'h-[38px] px-3.5 text-body-sm gap-2',
  md: 'h-11 px-[22px] text-body gap-2',
  lg: 'h-[46px] px-[28px] text-body gap-2',
  xl: 'h-12 px-6 text-body gap-2',
  hero: 'h-[50px] px-6 text-cta gap-2',
};

const VARIANT: Record<ButtonVariant, string> = {
  cta: 'bg-pohutukawa text-white border border-transparent hover:bg-pohutukawa-hover',
  // The export reuses the nav hover token here, which would put white text on a
  // beige fill. We darken ngahere instead — see docs/design-fidelity.md.
  primary: 'bg-ngahere text-white border border-transparent hover:bg-[#194B32]',
  secondary: 'bg-surface text-ink-2 border border-line hover:bg-hover',
  outline: 'bg-surface text-ngahere border border-ngahere-line hover:bg-hover',
  teal: 'bg-moana text-white border border-transparent hover:bg-moana-hover',
  tealSoft: 'bg-moana-tint text-moana-ink border border-moana-line hover:bg-moana-tint-hover',
  ctaSoft: 'bg-pohutukawa-tint text-pohutukawa border border-transparent hover:bg-[#FBE0D8]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'cta',
    size = 'lg',
    block = false,
    leadingIcon,
    trailingIcon,
    className,
    type = 'button',
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex cursor-pointer items-center justify-center rounded-2xl font-bold whitespace-nowrap',
        'transition-colors duration-fast ease-standard',
        'disabled:cursor-not-allowed disabled:opacity-40',
        SIZE[size],
        VARIANT[variant],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </button>
  );
});
